import React, { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { cloneDeep, keyBy } from 'lodash';

import CardDialog from '~/components/CardDialog';
import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import TextField from '~/components/core/Molecules/Fields/TextField';
import type { JsonEditorProps } from '~/components/core/Molecules/JsonEditor';
import JsonEditor from '~/components/core/Molecules/JsonEditor';
import Text from '~/components/core/TextComponents/Text';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { PolicyRequest } from '~/server_shared/openapi/data-contracts';
import { getLobDescription } from '~/Utils/lobUtils';

import type { PolicyServerRecord } from '../types/internal';
import type { LineOfBusiness, PolicyTemplateResponse, SubOrganizationInfo } from '../types/policy.types';

import PolicyTemplateSelector from './PolicyTemplateSelector';

interface PolicyFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit' | 'view';
  policy?: PolicyServerRecord | null;
  onClose: () => void;
  onSave: (policyRequest: PolicyRequest) => Promise<void>;
  loading?: boolean;
  error?: string | null;
}

const PolicyFormDialog: React.FC<PolicyFormDialogProps> = ({
  open,
  mode,
  policy,
  onClose,
  onSave,
  loading = false,
  error = null,
}) => {
  const { organization } = useSysconfig() as { organization: { sub_organizations?: SubOrganizationInfo[] } };
  const { lobConfigurationsDict } = useLobConfiguration();

  const [policyJsonData, setPolicyJsonData] = useState<Record<string, unknown>>({}); // Only holds policy_data portion
  const [displayName, setDisplayName] = useState<string>('');
  const [lob, setLob] = useState<string>('');
  const [subOrganizationId, setSubOrganizationId] = useState<string>('');
  const [originalPolicyNumber, setOriginalPolicyNumber] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A document that is not a plain object never reaches `policyJsonData` (see
  // `handlePolicyDataChange`), so Save has to be held back from the editor's own signals:
  // unparsable text arrives through `onValidationError`, but an emptied document (`null`) and a
  // parseable array/scalar are reported there as *zero* errors, so they are tracked from `onChange`.
  const [editorHasErrors, setEditorHasErrors] = useState(false);
  const [editorHasNonObjectDoc, setEditorHasNonObjectDoc] = useState(false);

  // Memoized sub-organization mapping for performance
  const subOrgNameMap = useMemo(() => {
    if (!organization?.sub_organizations) return {};
    return keyBy(organization.sub_organizations, 'external_id');
  }, [organization?.sub_organizations]);

  // Helper to get sub-organization name from external ID
  const getSubOrgName = (externalId: string): string => {
    if (!externalId) return 'Default';
    return subOrgNameMap[externalId]?.name || externalId;
  };

  // Helper to get LOB display name
  const getLobDisplayName = (lobValue: string): string => {
    return getLobDescription(lobValue.replace('_policy', '_claim'), lobConfigurationsDict) || lobValue;
  };

  // Helper to create contextual JSON section title
  const getJsonSectionTitle = (): string => {
    const baseTitle = mode === 'view' ? 'Policy Data' : 'Policy Data (JSON)';

    if (!lob) return baseTitle;

    const lobDisplayName = getLobDisplayName(lob);

    // Only show sub-org if organization has sub-organizations
    if (organization?.sub_organizations && organization.sub_organizations.length > 0) {
      const subOrgName = getSubOrgName(subOrganizationId);
      return `${baseTitle} • ${subOrgName} • ${lobDisplayName}`;
    }

    return `${baseTitle} • ${lobDisplayName}`;
  };

  // Initialize form data when dialog opens or policy changes
  useEffect(() => {
    if (open) {
      setEditorHasErrors(false);
      setEditorHasNonObjectDoc(false);
      if (mode === 'create') {
        setPolicyJsonData({});
        setDisplayName('');
        setLob('');
        setSubOrganizationId('');
        setOriginalPolicyNumber(null);
      } else if (policy) {
        // For edit/view mode, use the policy_data from PolicyContainer
        setPolicyJsonData((policy.policyContainer?.policy_data as Record<string, unknown>) || {});
        setDisplayName(policy.policyContainer?.display_name || policy.policyMetadata.display_name || '');
        setLob(policy.policyMetadata.lob || '');
        setSubOrganizationId(policy.policyMetadata.sub_organization_external_id || '');
        setOriginalPolicyNumber(policy.policyMetadata.policy_number || null);
      }
    }
  }, [open, mode, policy]);

  // Handle template selection
  const handleTemplateSelected = (template?: PolicyTemplateResponse | null) => {
    if (template) {
      // Set the entire template as the policy data
      setPolicyJsonData(template.template || {});
      setLob(template.lob || 'auto_policy');
      setSubOrganizationId(template.sub_organization_external_id?.toString() || '');
    } else {
      setPolicyJsonData({});
      setLob('');
      setSubOrganizationId('');
    }
  };

  const handlePolicyDataChange = (value: unknown) => {
    // The editor reports any parsed JSON, or null once its text is emptied; the form only holds a
    // plain object with keys (the render gate unmounts the editor over an empty one), so anything
    // else keeps the previous state and holds Save until a usable object is back.
    if (typeof value !== 'object' || value === null || Array.isArray(value) || Object.keys(value).length === 0) {
      setEditorHasNonObjectDoc(true);
      return;
    }
    setEditorHasNonObjectDoc(false);
    setPolicyJsonData(value as Record<string, unknown>);
  };

  const handleClose = () => {
    // Skip confirmation for view mode
    if (mode === 'view') {
      onClose();
      return;
    }

    // Check if there's any policy data that would be lost (only for create/edit modes)
    const hasPolicyData = Object.keys(policyJsonData).length > 0;
    if (hasPolicyData && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (mode === 'view') return;

    try {
      setIsSubmitting(true);
      const policyRequest: PolicyRequest = {
        display_name: displayName,
        policy: policyJsonData,
        lob,
        sub_organization_external_id: subOrganizationId,
      };
      await onSave(policyRequest);
    } catch (err) {
      // Error is handled by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case 'create':
        return 'Create New Policy';
      case 'edit':
        return `Edit Policy: ${originalPolicyNumber || 'Unknown'}`;
      case 'view':
        return `View Policy: ${policy?.policyMetadata.policy_number || 'Unknown'}`;
      default:
        return 'Policy';
    }
  };

  // Get mode-specific configuration for JsonEditor
  const getJsonEditorProps = (): JsonEditorProps => {
    const baseProps = {
      defaultValue: cloneDeep(policyJsonData),
      onChange: handlePolicyDataChange,
      history: true,
      onValidationError: (errors: ReadonlyArray<unknown>) => setEditorHasErrors(errors.length > 0),
    };

    switch (mode) {
      case 'create':
        return {
          ...baseProps,
          mode: 'form', // Primary: Best for exploring template structure
          allowedModes: ['tree', 'form', 'code'], // Allow switching between tree and code
          search: true, // Help users find specific fields
          navigationBar: true, // Show path navigation
          statusBar: true, // Show validation status
          sortObjectKeys: false, // Keep template structure as-is
          htmlElementProps: {
            style: { height: '100%' }, // Fill container height
          },
        };

      case 'edit':
        return {
          ...baseProps,
          mode: 'tree', // Primary: Familiar structure editing
          allowedModes: ['tree', 'form', 'code'], // More options for different user preferences
          search: true, // Critical for finding fields in large policies
          navigationBar: true, // Show path navigation
          statusBar: true, // Show validation status
          sortObjectKeys: true, // Consistent field ordering
          htmlElementProps: {
            style: { height: '100%' }, // Fill container height
          },
        };

      case 'view':
        return {
          ...baseProps,
          mode: 'view', // Primary: Pure read-only
          allowedModes: ['view', 'tree', 'code'], // Limited mode switching
          search: true, // Still allow searching
          navigationBar: true, // Show path navigation
          statusBar: false, // No need for validation status
          htmlElementProps: {
            style: { height: '100%' }, // Fill container height
          },
        };

      default:
        return baseProps;
    }
  };

  const canSave =
    mode !== 'view' &&
    !loading &&
    !isSubmitting &&
    !editorHasErrors &&
    !editorHasNonObjectDoc &&
    displayName.trim() !== '' &&
    lob.trim() !== '';

  const dialogFooterActions = (
    <DialogFooterActions
      onClickSecondary={handleClose}
      onClickPrimary={mode !== 'view' ? handleSave : undefined}
      primaryLabel={isSubmitting ? 'Saving...' : loading ? 'Loading...' : 'Save'}
      secondaryLabel={mode === 'view' ? 'Close' : 'Cancel'}
      disabled={loading || isSubmitting}
      primaryDisabled={!canSave}
      showPrimary={mode !== 'view'}
      ignorePermissionsSecondary={true}
    />
  );

  return (
    <CardDialog
      isDialog
      open={open}
      onClose={handleClose}
      title={getDialogTitle()}
      maxWidth="lg"
      fullWidth
      footerActions={dialogFooterActions}
    >
      <Grid container direction="column" spacing={3}>
        {/* Display Name Field - Always show first */}
        <Grid item>
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(newValue) => setDisplayName(newValue)}
            placeholder="Enter a descriptive name for this policy"
            fullWidth
            disabled={mode === 'view' || loading || isSubmitting}
            helperText={
              mode !== 'view'
                ? 'Provide a meaningful name to identify this policy (e.g., "Auto Policy - John Doe")'
                : undefined
            }
          />
        </Grid>

        {/* Template Selector - only show in create mode when no template selected */}
        {mode === 'create' && Object.keys(policyJsonData).length === 0 && (
          <Grid item>
            <PolicyTemplateSelector
              onTemplateSelected={handleTemplateSelected}
              disabled={loading || isSubmitting}
              initialLob={lob as LineOfBusiness}
              initialSubOrgId={subOrganizationId}
            />
          </Grid>
        )}

        {/* Policy JSON Editor - show only after template selection in create mode, or always in edit/view */}
        {((mode === 'create' && Object.keys(policyJsonData).length > 0) || mode === 'edit' || mode === 'view') && (
          <Grid item>
            <div className="space-y-2">
              <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM} className="text-gray-700">
                {getJsonSectionTitle()}
              </Text>
              {mode !== 'view' && (
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-gray-500 mb-4">
                  {mode === 'create'
                    ? 'Review and modify the policy template data. Use tree view to explore the structure or code view for direct JSON editing.'
                    : 'Edit the complete policy data. Use tree view for structured editing, form view for field-based editing, or code view for direct JSON editing.'}
                </Text>
              )}
              {Object.keys(policyJsonData).length > 0 && (
                <div className="border-gray-300 overflow-hidden rounded-md border" style={{ height: '450px' }}>
                  <JsonEditor {...getJsonEditorProps()} />
                </div>
              )}
            </div>
          </Grid>
        )}

        {/* Error Display - Below JSON editor, left-aligned */}
        {error && (
          <Grid item>
            <div className="border-red-200 bg-red-50 p-3 rounded-md border">
              <Text variant={Text.VARIANTS.SM} className="text-red-800">
                <strong>Error:</strong> {error}
              </Text>
            </div>
          </Grid>
        )}
      </Grid>
    </CardDialog>
  );
};

export default PolicyFormDialog;
